import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Select,
    MenuItem,
    FormControl,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    IconButton,
    alpha,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    Divider,
    Tooltip,
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    PersonAdd as PersonAddIcon,
    SwapHoriz as SwapHorizIcon,
    Undo as UndoIcon,
} from '@mui/icons-material';
import { confirmTrackEventImport } from 'api/TrackEvents';
import { getIndividuals, createMember } from 'api/Group';
import { useSnackbar } from 'notistack';
import useTheme from '@mui/system/useTheme';

interface TrackEventMatchReviewProps {
    open: boolean;
    onClose: () => void;
    results: any;
    group: any;
    importUrl: string;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`match-tabpanel-${index}`}
            aria-labelledby={`match-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

const TrackEventMatchReview: React.FC<TrackEventMatchReviewProps> = ({
    open,
    onClose,
    results,
    group,
    importUrl,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const [currentTab, setCurrentTab] = useState(0);
    const [importing, setImporting] = useState(false);

    const glassStyle = {
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    };

    // Track manual selections for multiple matches
    const [manualSelections, setManualSelections] = useState<Record<string, string>>({});

    // Track which auto-matched items are selected (only high confidence selected by default)
    const [selectedMatches, setSelectedMatches] = useState<Set<number>>(new Set());

    // Track overrides for auto-matched items (user changed the match)
    const [matchOverrides, setMatchOverrides] = useState<
        Record<number, { member_uuid: string; member_name: string }>
    >({});

    // State for adding members from unmatched
    const [addingMember, setAddingMember] = useState<number | null>(null);
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [showSearchDialog, setShowSearchDialog] = useState(false);
    const [selectedUnmatchedIndex, setSelectedUnmatchedIndex] = useState<number | null>(null);
    const [showingExactMatch, setShowingExactMatch] = useState(false);

    // State for reassigning auto-matched items
    const [reassignIndex, setReassignIndex] = useState<number | null>(null);
    const [showReassignDialog, setShowReassignDialog] = useState(false);
    const [reassignSearchResults, setReassignSearchResults] = useState<any[]>([]);
    const [reassignSearching, setReassignSearching] = useState(false);

    // Initialize only high confidence matches as selected when results change
    useEffect(() => {
        if (results?.matched) {
            const highConfidenceIndices = results.matched
                .map((match: any, index: number) => (match.confidence === 'high' ? index : null))
                .filter((index: number | null) => index !== null);
            setSelectedMatches(new Set(highConfidenceIndices));
        }
    }, [results]);

    // Auto-populate manual selections with 'SKIP' for all multiple matches
    useEffect(() => {
        if (results?.multiple_matches) {
            const skipSelections: Record<string, string> = {};
            results.multiple_matches.forEach((_: any, index: number) => {
                skipSelections[index.toString()] = 'SKIP';
            });
            setManualSelections(skipSelections);
        }
    }, [results]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const handleManualSelection = (index: number, memberUuid: string) => {
        setManualSelections((prev) => ({
            ...prev,
            [index.toString()]: memberUuid,
        }));
    };

    const handleToggleMatch = (index: number) => {
        setSelectedMatches((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        setSelectedMatches(new Set(results.matched.map((_: any, index: number) => index)));
    };

    const handleDeselectAll = () => {
        setSelectedMatches(new Set());
    };

    const handleOpenReassign = async (index: number, match: any) => {
        setReassignIndex(index);
        setReassignSearching(true);
        setShowReassignDialog(true);

        try {
            const results = await searchIndividual(match.athlete_name);
            setReassignSearchResults(results);
        } catch {
            setReassignSearchResults([]);
        } finally {
            setReassignSearching(false);
        }
    };

    const handleReassignMatch = (index: number, memberUuid: string, memberName: string) => {
        setMatchOverrides((prev) => ({
            ...prev,
            [index]: { member_uuid: memberUuid, member_name: memberName },
        }));
        // Ensure the item stays selected
        setSelectedMatches((prev) => {
            const newSet = new Set(prev);
            newSet.add(index);
            return newSet;
        });
        setShowReassignDialog(false);
        setReassignIndex(null);
    };

    const handleUndoReassign = (index: number) => {
        setMatchOverrides((prev) => {
            const next = { ...prev };
            delete next[index];
            return next;
        });
    };

    const searchIndividual = async (athleteName: string) => {
        try {
            const response = await getIndividuals({
                'filter[name]': athleteName,
                'filter[organization]': group.organization.uuid,
                limit: 10,
            });
            return response.data.data || [];
        } catch (err) {
            enqueueSnackbar('Failed to search for individuals', { variant: 'error' });
            return [];
        }
    };

    const handleOpenAddMember = async (index: number, athlete: any) => {
        setSelectedUnmatchedIndex(index);

        // Search for existing individuals with this name
        const results = await searchIndividual(athlete.athlete_name);

        // Normalize the athlete name for comparison (remove extra spaces, lowercase)
        const normalizeNameForComparison = (name: string) => {
            return name.trim().toLowerCase().replace(/\s+/g, ' ').replace(/,\s*/g, ', ');
        };

        const searchName = normalizeNameForComparison(athlete.athlete_name);

        // Find exact matches
        const exactMatches = results.filter((individual: any) => {
            const individualName = normalizeNameForComparison(individual.name);
            return individualName === searchName;
        });

        // Find near matches (not exact)
        const nearMatches = results.filter((individual: any) => {
            const individualName = normalizeNameForComparison(individual.name);
            return individualName !== searchName;
        });

        // If there's exactly one unique exact match, show only that
        let filteredResults;
        if (exactMatches.length === 1) {
            filteredResults = exactMatches;
            setShowingExactMatch(true);
        } else {
            // Otherwise show exact matches first, then near matches
            filteredResults = [...exactMatches, ...nearMatches];
            setShowingExactMatch(false);
        }

        setSearchResults(filteredResults);
        setShowSearchDialog(true);
    };

    const handleAddMemberFromUnmatched = async (
        unmatchedItem: any,
        individualUuid?: string,
        existingIndividual?: any,
    ) => {
        try {
            setAddingMember(selectedUnmatchedIndex);

            // If linking an existing individual, use their name; otherwise parse from athlete_name
            let firstName: string;
            let lastName: string;

            if (existingIndividual) {
                // Use the existing individual's name
                // Individual.name is typically "FirstName LastName", but handle various formats
                const name = existingIndividual.name || '';
                const nameParts = name.trim().split(/\s+/);
                if (nameParts.length >= 2) {
                    firstName = nameParts[0];
                    lastName = nameParts.slice(1).join(' ');
                } else {
                    // Fallback: use full name as first name
                    firstName = name;
                    lastName = '';
                }
            } else {
                const athleteName = (unmatchedItem.athlete_name || '').trim();
                if (athleteName.includes(',')) {
                    // MileSplit format: "LastName, FirstName"
                    const nameParts = athleteName.split(',').map((p: string) => p.trim());
                    lastName = nameParts[0] || '';
                    firstName = nameParts[1] || '';
                } else {
                    // Athletic.net format: "FirstName LastName"
                    const nameParts = athleteName.split(/\s+/);
                    if (nameParts.length >= 2) {
                        firstName = nameParts[0];
                        lastName = nameParts.slice(1).join(' ');
                    } else {
                        firstName = athleteName;
                        lastName = '';
                    }
                }
            }

            // Get default member type (use first member type from group, or null if none exist)
            const memberType = group.member_types?.[0];

            const response = await createMember(group.uuid, {
                first_name: firstName,
                last_name: lastName,
                attributes: [],
                member_type: memberType
                    ? {
                          name: memberType.name,
                          uuid: memberType.uuid,
                      }
                    : null,
                individual: individualUuid || null,
                skip_duplicate_check: true,
            });

            const newMember = response.data;

            enqueueSnackbar(
                individualUuid
                    ? 'Existing individual added to group'
                    : 'New member created and added to group',
                { variant: 'success' },
            );

            // Add to manual selections so it can be imported
            if (selectedUnmatchedIndex !== null) {
                setManualSelections((prev) => ({
                    ...prev,
                    [`unmatched_${selectedUnmatchedIndex}`]: newMember.uuid,
                }));
            }

            // Close dialog
            setShowSearchDialog(false);
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to add member', {
                variant: 'error',
            });
        } finally {
            setAddingMember(null);
        }
    };

    const handleConfirmImport = async () => {
        try {
            setImporting(true);

            // Filter to only include selected matches, applying any overrides
            const selectedAutoMatches = results.matched
                .filter((_: any, index: number) => selectedMatches.has(index))
                .map((match: any, index: number) => {
                    const override = matchOverrides[index];
                    return {
                        ...match,
                        ...(override
                            ? {
                                  member_uuid: override.member_uuid,
                                  member_name: override.member_name,
                              }
                            : {}),
                        meet_name: results.meet_info?.meet_name,
                    };
                });

            // Combine all matches
            const allMatches = [
                ...selectedAutoMatches,
                // Add manually resolved multiple matches (skip those marked as 'SKIP' or not selected)
                ...results.multiple_matches
                    .map((item: any, index: number) => {
                        const selectedUuid = manualSelections[index.toString()];
                        // Skip if not selected or explicitly marked as SKIP
                        if (!selectedUuid || selectedUuid === 'SKIP') return null;

                        const selectedMember = item.potential_matches.find(
                            (m: any) => m.member_uuid === selectedUuid,
                        );

                        return {
                            athlete_name: item.athlete_name,
                            member_uuid: selectedUuid,
                            member_name: selectedMember?.member_name,
                            event: item.event,
                            mark: item.mark,
                            school: item.school,
                            year: item.year,
                            date: item.date,
                            location: item.location,
                            meet_name: results.meet_info?.meet_name,
                        };
                    })
                    .filter(Boolean),
                // Add newly created members from unmatched
                ...Object.keys(manualSelections)
                    .filter((key) => key.startsWith('unmatched_'))
                    .map((key) => {
                        const index = parseInt(key.replace('unmatched_', ''), 10);
                        const unmatchedItem = results.unmatched[index];
                        const memberUuid = manualSelections[key];

                        if (!unmatchedItem || !memberUuid) return null;

                        return {
                            athlete_name: unmatchedItem.athlete_name,
                            member_uuid: memberUuid,
                            event: unmatchedItem.event,
                            mark: unmatchedItem.mark,
                            school: unmatchedItem.school,
                            year: unmatchedItem.year,
                            date: unmatchedItem.date,
                            location: unmatchedItem.location,
                            meet_name: results.meet_info?.meet_name,
                        };
                    })
                    .filter(Boolean),
            ];

            const response = await confirmTrackEventImport({
                group_uuid: group.uuid,
                matches: allMatches,
                import_url: importUrl,
            });

            enqueueSnackbar(response.data.message, { variant: 'success' });
            onClose();
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to import performances', {
                variant: 'error',
            });
        } finally {
            setImporting(false);
        }
    };

    const canImport = () => {
        // Allow import as long as there's at least one match to import
        const selectedMatchedCount = selectedMatches.size;
        const resolvedMultipleMatches = Object.keys(manualSelections).filter(
            (key) => !key.startsWith('unmatched_') && manualSelections[key] !== 'SKIP',
        ).length;
        const addedUnmatchedCount = Object.keys(manualSelections).filter((key) =>
            key.startsWith('unmatched_'),
        ).length;
        return selectedMatchedCount + resolvedMultipleMatches + addedUnmatchedCount > 0;
    };

    const matchedCount = results.matched?.length || 0;
    const selectedMatchedCount = selectedMatches.size;
    const multipleMatchCount = results.multiple_matches?.length || 0;
    const unmatchedCount = results.unmatched?.length || 0;
    // Count only non-SKIP manual selections (exclude unmatched additions)
    const resolvedMultipleMatches = Object.keys(manualSelections).filter(
        (key) =>
            !key.startsWith('unmatched_') &&
            manualSelections[key] &&
            manualSelections[key] !== 'SKIP',
    ).length;
    const skippedCount = Object.keys(manualSelections).filter(
        (key) => !key.startsWith('unmatched_') && manualSelections[key] === 'SKIP',
    ).length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: glassStyle,
            }}
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Review Import Matches</Typography>
                    <IconButton onClick={onClose} disabled={importing}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {/* Summary */}
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2" gutterBottom>
                        <strong>Meet:</strong> {results.meet_info?.meet_name || 'Unknown'}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Date:</strong> {results.meet_info?.date || 'Unknown'} •{' '}
                        <strong>Location:</strong> {results.meet_info?.location || 'Unknown'}
                    </Typography>
                    <Box mt={1}>
                        <Chip
                            icon={<CheckCircleIcon />}
                            label={`${selectedMatchedCount} of ${matchedCount} Auto-matched selected`}
                            color="success"
                            size="small"
                            sx={{ mr: 1 }}
                        />
                        {multipleMatchCount > 0 && (
                            <Chip
                                icon={<WarningIcon />}
                                label={`${multipleMatchCount} Need Review (${resolvedMultipleMatches} resolved)`}
                                color="warning"
                                size="small"
                                sx={{ mr: 1 }}
                            />
                        )}
                        {unmatchedCount > 0 && (
                            <Chip
                                label={`${unmatchedCount} Unmatched`}
                                color="default"
                                size="small"
                            />
                        )}
                    </Box>
                </Alert>

                {/* Tabs */}
                <Box
                    sx={{
                        borderBottom: 2,
                        borderColor: 'divider',
                        bgcolor: alpha('#f5f5f5', 0.6),
                        borderRadius: '8px 8px 0 0',
                        px: 1,
                        pt: 0.5,
                    }}
                >
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        sx={{
                            '& .MuiTab-root': {
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                textTransform: 'none',
                                minHeight: 48,
                                borderRadius: '8px 8px 0 0',
                                mx: 0.5,
                                transition: 'all 0.2s',
                            },
                            '& .Mui-selected': {
                                bgcolor: 'background.paper',
                                boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
                            },
                            '& .MuiTabs-indicator': {
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                            },
                        }}
                    >
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                    {`Auto-Matched (${selectedMatchedCount}/${matchedCount})`}
                                </Box>
                            }
                        />
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                                    {`Needs Review (${multipleMatchCount})`}
                                </Box>
                            }
                            disabled={multipleMatchCount === 0}
                        />
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />
                                    {`Unmatched (${unmatchedCount})`}
                                    {unmatchedCount > 0 && (
                                        <Chip
                                            label={unmatchedCount}
                                            size="small"
                                            color="error"
                                            sx={{
                                                height: 22,
                                                fontWeight: 800,
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                    )}
                                </Box>
                            }
                            disabled={unmatchedCount === 0}
                            sx={
                                unmatchedCount > 0
                                    ? {
                                          '&:not(.Mui-selected)': {
                                              animation: 'pulse-tab 2s ease-in-out 3',
                                          },
                                          '@keyframes pulse-tab': {
                                              '0%, 100%': { bgcolor: 'transparent' },
                                              '50%': { bgcolor: alpha('#f44336', 0.08) },
                                          },
                                      }
                                    : {}
                            }
                        />
                    </Tabs>
                </Box>

                {/* Auto-Matched Tab */}
                <TabPanel value={currentTab} index={0}>
                    {matchedCount > 0 && (
                        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                            <Button size="small" variant="outlined" onClick={handleSelectAll}>
                                Select All
                            </Button>
                            <Button size="small" variant="outlined" onClick={handleDeselectAll}>
                                Deselect All
                            </Button>
                        </Box>
                    )}
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={
                                                selectedMatchedCount === matchedCount &&
                                                matchedCount > 0
                                            }
                                            indeterminate={
                                                selectedMatchedCount > 0 &&
                                                selectedMatchedCount < matchedCount
                                            }
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    handleSelectAll();
                                                } else {
                                                    handleDeselectAll();
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>Athlete</TableCell>
                                    <TableCell>Matched Member</TableCell>
                                    <TableCell>Event</TableCell>
                                    <TableCell>Mark</TableCell>
                                    <TableCell>School</TableCell>
                                    <TableCell>Year</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {results.matched?.map((match: any, index: number) => {
                                    const override = matchOverrides[index];
                                    return (
                                        <TableRow
                                            key={index}
                                            sx={
                                                override
                                                    ? {
                                                          bgcolor: alpha(
                                                              theme.palette.info.main,
                                                              0.05,
                                                          ),
                                                      }
                                                    : {}
                                            }
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedMatches.has(index)}
                                                    onChange={() => handleToggleMatch(index)}
                                                />
                                            </TableCell>
                                            <TableCell>{match.athlete_name}</TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    {override ? (
                                                        <>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    textDecoration: 'line-through',
                                                                    color: 'text.disabled',
                                                                    mr: 0.5,
                                                                }}
                                                            >
                                                                {match.member_name}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight={600}
                                                                color="info.main"
                                                            >
                                                                {override.member_name}
                                                            </Typography>
                                                            <Chip
                                                                label="Reassigned"
                                                                size="small"
                                                                color="info"
                                                                sx={{ ml: 0.5 }}
                                                            />
                                                            <Tooltip title="Undo reassignment">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                        handleUndoReassign(index)
                                                                    }
                                                                >
                                                                    <UndoIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {match.member_name}
                                                            <Chip
                                                                label={match.confidence}
                                                                size="small"
                                                                color={
                                                                    match.confidence === 'high'
                                                                        ? 'success'
                                                                        : 'default'
                                                                }
                                                                sx={{ ml: 0.5 }}
                                                            />
                                                            <Tooltip title="Wrong match? Reassign to a different member">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                        handleOpenReassign(
                                                                            index,
                                                                            match,
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        ml: 0.5,
                                                                        color: 'text.secondary',
                                                                    }}
                                                                >
                                                                    <SwapHorizIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{match.event}</TableCell>
                                            <TableCell>
                                                <strong>{match.mark}</strong>
                                            </TableCell>
                                            <TableCell>{match.school}</TableCell>
                                            <TableCell>{match.year}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Multiple Matches Tab */}
                <TabPanel value={currentTab} index={1}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        These athletes matched multiple group members. Please select the correct
                        match for each.
                    </Alert>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Athlete</TableCell>
                                    <TableCell>Event / Mark</TableCell>
                                    <TableCell>Select Member</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {results.multiple_matches?.map((item: any, index: number) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {item.athlete_name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {item.school} • {item.year}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{item.event}</Typography>
                                            <Typography variant="body2">
                                                <strong>{item.mark}</strong>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={
                                                        manualSelections[index.toString()] || 'SKIP'
                                                    }
                                                    onChange={(e) =>
                                                        handleManualSelection(index, e.target.value)
                                                    }
                                                    displayEmpty
                                                >
                                                    <MenuItem
                                                        value="SKIP"
                                                        sx={{
                                                            fontStyle: 'italic',
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        Skip this athlete (don&apos;t import)
                                                    </MenuItem>
                                                    {item.potential_matches.map((match: any) => (
                                                        <MenuItem
                                                            key={match.member_uuid}
                                                            value={match.member_uuid}
                                                        >
                                                            {match.member_name} ({match.confidence},
                                                            score: {match.score.toFixed(2)})
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Unmatched Tab */}
                <TabPanel value={currentTab} index={2}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        These athletes could not be matched to any group member. You can add them to
                        the group to enable import.
                    </Alert>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Athlete</TableCell>
                                    <TableCell>Event</TableCell>
                                    <TableCell>Mark</TableCell>
                                    <TableCell>School</TableCell>
                                    <TableCell>Year</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {results.unmatched?.map((item: any, index: number) => {
                                    const isAdded = manualSelections[`unmatched_${index}`];
                                    return (
                                        <TableRow key={index}>
                                            <TableCell>{item.athlete_name}</TableCell>
                                            <TableCell>{item.event}</TableCell>
                                            <TableCell>{item.mark}</TableCell>
                                            <TableCell>{item.school}</TableCell>
                                            <TableCell>{item.year}</TableCell>
                                            <TableCell>
                                                {isAdded ? (
                                                    <Chip
                                                        label="Added"
                                                        color="success"
                                                        size="small"
                                                        icon={<CheckCircleIcon />}
                                                    />
                                                ) : (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<PersonAddIcon />}
                                                        onClick={() =>
                                                            handleOpenAddMember(index, item)
                                                        }
                                                        disabled={addingMember !== null}
                                                    >
                                                        Add to Group
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={importing}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirmImport}
                    variant="contained"
                    color="primary"
                    disabled={importing || !canImport()}
                    startIcon={importing ? <CircularProgress size={20} /> : undefined}
                >
                    {(() => {
                        if (importing) return 'Importing...';
                        const addedUnmatchedCount = Object.keys(manualSelections).filter((key) =>
                            key.startsWith('unmatched_'),
                        ).length;
                        const totalToImport =
                            selectedMatchedCount + resolvedMultipleMatches + addedUnmatchedCount;
                        const performanceText = `Import ${totalToImport} Performance${
                            totalToImport !== 1 ? 's' : ''
                        }`;
                        const pendingCount =
                            multipleMatchCount - resolvedMultipleMatches - skippedCount;
                        if (skippedCount > 0) {
                            return `${performanceText} (${skippedCount} skipped)`;
                        }
                        if (pendingCount > 0) {
                            return `${performanceText} (${pendingCount} pending)`;
                        }
                        return performanceText;
                    })()}
                </Button>
            </DialogActions>

            {/* Reassign Auto-Match Dialog */}
            <Dialog
                open={showReassignDialog}
                onClose={() => {
                    setShowReassignDialog(false);
                    setReassignIndex(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Reassign Match
                    {reassignIndex !== null && results.matched?.[reassignIndex] && (
                        <Typography variant="body2" color="text.secondary">
                            Athlete: <strong>{results.matched[reassignIndex].athlete_name}</strong>
                            {' — '}
                            {results.matched[reassignIndex].event}{' '}
                            {results.matched[reassignIndex].mark}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {reassignSearching ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress />
                        </Box>
                    ) : reassignSearchResults.length > 0 ? (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Select the correct member for this athlete:
                            </Alert>
                            <List>
                                {reassignSearchResults.map((individual) => (
                                    <ListItem
                                        key={individual.uuid}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            mb: 1,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                            },
                                        }}
                                        onClick={() => {
                                            if (reassignIndex !== null) {
                                                handleReassignMatch(
                                                    reassignIndex,
                                                    individual.uuid,
                                                    individual.name,
                                                );
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={individual.name}
                                            secondary={`In ${
                                                individual.groups?.length || 0
                                            } group(s)`}
                                        />
                                        <Button variant="outlined" size="small">
                                            Select
                                        </Button>
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    ) : (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            No other individuals found with a similar name. You can uncheck this row
                            to skip it, or use the Unmatched tab to add a new member first.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowReassignDialog(false);
                            setReassignIndex(null);
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Member Search Dialog */}
            <Dialog
                open={showSearchDialog}
                onClose={() => setShowSearchDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add Member to Group</DialogTitle>
                <DialogContent>
                    {searchResults.length > 0 ? (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {showingExactMatch
                                    ? 'Found 1 exact match in your organization. Link this individual or create a new one.'
                                    : `Found ${searchResults.length} existing individual(s) with similar names in your organization. Link one of these or create a new individual.`}
                            </Alert>
                            <List>
                                {searchResults.map((individual) => (
                                    <ListItem
                                        key={individual.uuid}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <ListItemText
                                            primary={individual.name}
                                            secondary={`In ${
                                                individual.groups?.length || 0
                                            } group(s)`}
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (selectedUnmatchedIndex !== null) {
                                                    const unmatchedItem =
                                                        results.unmatched[selectedUnmatchedIndex];
                                                    handleAddMemberFromUnmatched(
                                                        unmatchedItem,
                                                        individual.uuid,
                                                        individual,
                                                    );
                                                }
                                            }}
                                            disabled={addingMember !== null}
                                        >
                                            {addingMember !== null ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                'Link Existing'
                                            )}
                                        </Button>
                                    </ListItem>
                                ))}
                            </List>
                            <Divider sx={{ my: 2 }} />
                        </>
                    ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            No existing individuals found with this name in your organization.
                        </Alert>
                    )}
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                            if (selectedUnmatchedIndex !== null) {
                                const unmatchedItem = results.unmatched[selectedUnmatchedIndex];
                                handleAddMemberFromUnmatched(unmatchedItem);
                            }
                        }}
                        disabled={addingMember !== null}
                        startIcon={
                            addingMember !== null ? (
                                <CircularProgress size={20} />
                            ) : (
                                <PersonAddIcon />
                            )
                        }
                    >
                        {addingMember !== null
                            ? 'Creating...'
                            : 'Create New Individual & Add to Group'}
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setShowSearchDialog(false)}
                        disabled={addingMember !== null}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default TrackEventMatchReview;
